<script lang="ts">
  import type { ToastAction, ToastKind } from './toast-queue.svelte';
  import { toastDuration } from './toast-queue.svelte';

  interface Props {
    message: string;
    kind?: ToastKind;
    action?: ToastAction;
    durationMs?: number;
    onclose?: (() => void) | undefined;
  }

  let { message, kind = 'info', action, durationMs, onclose }: Props = $props();

  const total = $derived(toastDuration({ action, durationMs }));
  let paused = $state(false);
  let hovered = false;
  let focused = false;

  // ホバー・フォーカス中はタイマーを止める（WCAG 2.2.1）。止めた時点の残り時間から再開する
  let remaining = -1;
  $effect(() => {
    if (remaining < 0) remaining = total;
    if (paused || remaining <= 0) return;
    const started = performance.now();
    const timer = setTimeout(() => onclose?.(), remaining);
    return () => {
      clearTimeout(timer);
      remaining = Math.max(0, remaining - (performance.now() - started));
    };
  });

  function updatePause() {
    paused = hovered || focused;
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    // 開いているものから閉じる。ドリルの中断より先に処理する
    e.preventDefault();
    e.stopPropagation();
    onclose?.();
  }

  const role = $derived(kind === 'warn' || kind === 'err' ? 'alert' : 'status');
</script>

<svelte:window onkeydowncapture={onkeydown} />

<div
  class="toast toast-{kind}"
  {role}
  data-testid="toast"
  onmouseenter={() => {
    hovered = true;
    updatePause();
  }}
  onmouseleave={() => {
    hovered = false;
    updatePause();
  }}
  onfocusin={() => {
    focused = true;
    updatePause();
  }}
  onfocusout={() => {
    focused = false;
    updatePause();
  }}
>
  <svg class="icon" viewBox="0 0 20 20" aria-hidden="true">
    {#if kind === 'ok'}
      <path
        d="M4 10.5l4 4 8-9"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    {:else if kind === 'warn' || kind === 'err'}
      <path
        d="M10 2.8l7.6 13.4H2.4z"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linejoin="round"
      />
      <path d="M10 8v3.6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      <circle cx="10" cy="14" r="1.1" fill="currentColor" />
    {:else}
      <circle cx="10" cy="10" r="7.5" fill="none" stroke="currentColor" stroke-width="1.8" />
      <path d="M10 9v5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      <circle cx="10" cy="6" r="1.1" fill="currentColor" />
    {/if}
  </svg>
  <p class="message">{message}</p>
  {#if action}
    <button
      type="button"
      class="btn btn-sm btn-quiet"
      onclick={() => {
        action.run();
        onclose?.();
      }}>{action.label}</button
    >
  {/if}
  <button type="button" class="close" aria-label="通知を閉じる" onclick={() => onclose?.()}>
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M6 6l8 8M14 6l-8 8"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  </button>
</div>

<style>
  .toast {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    max-width: min(32rem, calc(100vw - 2 * var(--space-4)));
    padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface-raised);
    box-shadow: var(--shadow-lift);
    color: var(--text);
    font-size: var(--fs-sm);
    line-height: var(--lh-ui);
    animation: toast-in var(--dur-base) var(--ease-out);
  }
  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateY(var(--space-2));
    }
  }
  .icon {
    width: 1.25rem;
    height: 1.25rem;
    flex: none;
  }
  .toast-info .icon {
    color: var(--info-text);
  }
  .toast-ok .icon {
    color: var(--ok-text);
  }
  .toast-warn .icon {
    color: var(--warn-text);
  }
  .toast-err .icon {
    color: var(--err-text);
  }
  .message {
    flex: 1;
    min-width: 0;
    padding: var(--space-1) 0;
  }
  .close {
    display: inline-grid;
    place-items: center;
    flex: none;
    width: var(--tap-min);
    height: var(--tap-min);
    border: 0;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-soft);
  }
  .close:hover {
    color: var(--text);
  }
  .close svg {
    width: 1rem;
    height: 1rem;
  }
  :global(html[data-motion='reduce']) .toast {
    animation: none;
  }
</style>
